// Печатный бланк заявления о реквизитах (тз, раздел 5.4.6: «печатные формы формируются по
// образцам Заказчика»).
//
// ## Что печатается и почему именно так
//
// Бланки заказчик передал сам — два файла в «От клиента/Бланки заявлений». Оба рассчитаны на
// заполнение РУКОЙ: банк, номер карты и подпись человек вписывает сам, глядя на свою карту.
// Поэтому печатается пустой бланк с заполненной шапкой, а не готовый документ из данных
// системы: в системе реквизиты появляются ПОСЛЕ того, как это заявление подписано, и печатать
// их обратно означало бы менять местами причину и следствие.
//
// Шапка заполняется тем, что система знает наверняка: кому адресовано (подписант юрлица из
// настроек) и от кого (человек из карточки). Ровно это кадровик и переписывает руками сегодня.
//
// ## Полного номера карты здесь нет и быть не может
//
// Он хранится зашифрованным и не отдаётся наружу ни одним маршрутом. Печатать «···· 5678» в
// заявлении, по которому будут переводить деньги, бессмысленно: такой лист банк не примет и
// подписывать его человеку незачем.

import { useState } from 'react';


import { PrintButton } from '@/shared/reports/PrintButton';
import type { LegalEntityOut } from '@/shared/api/endpoints/org';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { Block } from '@/shared/ui/block';
import * as SegmentedControl from '@/shared/ui/segmented-control';

/** Два бланка заказчика. Различает их не оформление, а состав полей и основание. */
type ВидБланка = 'обычный' | 'официальный';

const ВИДЫ: ReadonlyArray<{ код: ВидБланка; подпись: string; пояснение: string }> = [
  {
    код: 'обычный',
    подпись: 'Обычный',
    пояснение: 'Карта или перевод по номеру телефона. Получателем может быть другой человек',
  },
  {
    код: 'официальный',
    подпись: 'Официальный',
    пояснение: 'Со ссылкой на статью 136 ТК и полными банковскими реквизитами',
  },
];

/** Строка бланка, которую человек заполняет рукой. Длина — как в файле заказчика. */
function Прочерк({ подпись }: { подпись: string }) {
  return (
    <p className='mb-2 text-paragraph-sm text-text-strong-950'>
      {подпись}{' '}
      <span className='inline-block border-b border-stroke-strong-950 align-baseline print:min-w-96'>
        {' '.repeat(60)}
      </span>
    </p>
  );
}

export function БланкЗаявления() {
  const [вид, setВид] = useState<ВидБланка>('обычный');
  const юрлица = useLegalEntitiesQuery();
  const юрлицо = юрлица.data?.items[0];

  return (
    <Block
      title='Бланк заявления'
      note='Печатается по образцу заказчика: человек заполняет банковские поля рукой и подписывает'
    >
      <div data-печать='скрыть' className='mb-4 flex flex-wrap items-center gap-3'>
        <SegmentedControl.Root
          value={вид}
          onValueChange={(значение) => setВид(значение as ВидБланка)}
        >
          <SegmentedControl.List>
            {ВИДЫ.map(({ код, подпись }) => (
              <SegmentedControl.Trigger key={код} value={код}>
                {подпись}
              </SegmentedControl.Trigger>
            ))}
          </SegmentedControl.List>
        </SegmentedControl.Root>

        <PrintButton />

        <p className='w-full text-paragraph-sm text-text-sub-600'>
          {ВИДЫ.find((в) => в.код === вид)?.пояснение}
        </p>
      </div>

      <Бланк вид={вид} юрлицо={юрлицо} />
    </Block>
  );
}

function Бланк({ вид, юрлицо }: { вид: ВидБланка; юрлицо: LegalEntityOut | undefined }) {
  const адресат = кому(юрлицо);

  return (
    // Помечен документом: печать разворачивает его целиком, а не печатает видимый кусок.
    <article
      data-печать='документ'
      className='mx-auto max-w-3xl rounded-2xl border border-stroke-soft-200 bg-bg-white-0 p-8'
    >
      <div className='mb-8 ml-auto w-72 text-paragraph-sm text-text-strong-950'>
        {адресат.map((строка) => (
          <p key={строка}>{строка}</p>
        ))}
        {/* «от» остаётся прочерком, как в бланке заказчика, и это не лень.

            Заявление пишут ОТ КОГО — родительный падеж: «от Багровева Романа Фёдоровича».
            Просклонять фамилию программа не может, а «от Багровев Роман Фёдорович» на
            подписываемом листе читается как небрежность к самому человеку. Должность тем
            более: в карточке лежит назначение на пост, а в заявлении пишут должность по
            трудовому договору, и это не всегда одно и то же. */}
        <p className='mt-4'>от</p>
        <p className='mt-3 border-b border-stroke-strong-950'>&nbsp;</p>
        <p className='text-paragraph-xs text-text-sub-600'>должность</p>
        <p className='mt-3 border-b border-stroke-strong-950'>&nbsp;</p>
        <p className='text-paragraph-xs text-text-sub-600'>фамилия, имя, отчество</p>
      </div>

      <h3 className='mb-1 text-center text-label-md text-text-strong-950'>ЗАЯВЛЕНИЕ</h3>
      <p className='mb-6 text-center text-paragraph-sm text-text-strong-950'>
        о перечислении заработной платы на банковскую карту
      </p>

      {вид === 'обычный' ? <ОбычныйБланк /> : <ОфициальныйБланк />}

      <div className='mt-10 flex items-end justify-between gap-6 text-paragraph-xs text-text-sub-600'>
        <span className='w-32 border-t border-stroke-strong-950 pt-1 text-center'>дата</span>
        <span className='w-40 border-t border-stroke-strong-950 pt-1 text-center'>подпись</span>
        <span className='w-56 border-t border-stroke-strong-950 pt-1 text-center'>
          фамилия, инициалы
        </span>
      </div>
    </article>
  );
}

function ОбычныйБланк() {
  return (
    <>
      <p className='mb-4 text-paragraph-sm text-text-strong-950'>
        Прошу перечислять причитающиеся мне выплаты на указанные ниже банковские реквизиты
        (банковскую карту либо по системе быстрых платежей):
      </p>
      <Прочерк подпись='Банк получателя' />
      <Прочерк подпись='Номер карты' />
      <p className='mb-2 mt-4 text-paragraph-sm text-text-strong-950'>
        Либо через систему быстрых платежей:
      </p>
      <Прочерк подпись='Номер телефона' />
      <Прочерк подпись='Банк для получения переводов' />
      <Прочерк подпись='ФИО получателя' />
      <p className='mt-4 text-paragraph-sm text-text-strong-950'>
        Ответственность за правильность предоставленных банковских реквизитов, номера телефона и
        выбранного банка несу лично. При изменении номера карты, номера телефона либо банка обязуюсь
        незамедлительно уведомить работодателя путём подачи нового заявления.
      </p>
    </>
  );
}

function ОфициальныйБланк() {
  return (
    <>
      <p className='mb-4 text-paragraph-sm text-text-strong-950'>
        На основании статьи 136 Трудового кодекса прошу перечислять мою заработную плату по
        следующим реквизитам:
      </p>
      <Прочерк подпись='Банк получателя' />
      <Прочерк подпись='Номер карты' />
      <Прочерк подпись='ФИО держателя карты' />
      <Прочерк подпись='БИК' />
      <Прочерк подпись='Корреспондентский счёт' />
      <Прочерк подпись='Расчётный счёт' />
      <p className='mt-4 text-paragraph-sm text-text-strong-950'>
        Копию реквизитов счёта прилагаю на бумажном носителе.
      </p>
    </>
  );
}

/**
 * Кому адресовано заявление.
 *
 * «Руководителю», а не должность из настроек, и это осознанно: шапка заявления стоит в
 * дательном падеже — «Генеральному директору», — а в настройках должность записана
 * именительным, потому что оттуда же она уходит в шапку ведомости («Генеральный директор
 * Шатун А. В.»). Просклонять произвольную строку программа не может, а два поля на одну
 * должность разъедутся на первой правке. «Руководителю» верно всегда.
 *
 * Название и фамилию берём как есть: они в шапке заявления не склоняются.
 */
function кому(юрлицо: LegalEntityOut | undefined): string[] {
  const строки = ['Руководителю'];
  if (юрлицо?.name != null && юрлицо.name !== '') {
    строки.push(юрлицо.name);
  }
  if (юрлицо?.signer_name != null && юрлицо.signer_name !== '') {
    строки.push(юрлицо.signer_name);
  }
  return строки;
}
